import type { CompetitorRecord } from "@/lib/compare/types";

const HOME = "https://clippie.ai";
const PRICING = "https://clippie.ai/pricing";
const REFUNDS = "https://clippie.ai/refund-policy";
const CHECKED = "2026-10-08";

export const CLIPPIE: CompetitorRecord = {
  slug: "clippie",
  name: "Clippie AI",
  aliases: ["Clippie", "clippie.ai", "Clippie.ai"],
  url: HOME,
  pricingUrl: PRICING,
  category: "faceless",
  positioning: "Viral faceless videos, 100x faster with AI: a bundle of faceless templates plus an AI Studio.",
  plans: [
    {
      name: "Lite",
      monthly: 19.99,
      metered:
        "300 AI Studio credits, 30 export minutes, 30 AI voice minutes, 30 subtitle minutes, 100 AI images, 1 custom voice",
      includes: [
        "All templates",
        "Captions in 102 languages",
        "Access to 50+ AI voices",
        "1 custom voice slot",
        "24/7 support",
        "Renews monthly until cancelled",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Monthly figure only. The annual toggle says 25% off but no annual price rendered on the page.",
    },
    {
      name: "Creator",
      monthly: 34.99,
      metered:
        "1,000 AI Studio credits, 120 export minutes, 120 AI voice minutes, 120 subtitle minutes, 500 AI images, 10 custom voices",
      includes: [
        "All templates",
        "Captions in 102 languages",
        "Access to 50+ AI voices",
        "10 custom voice slots",
        "Renews monthly until cancelled",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Monthly figure only. The annual toggle says 25% off but no annual price rendered on the page.",
    },
    {
      name: "Pro",
      monthly: 69.99,
      metered:
        "2,000 AI Studio credits, 250 export minutes, 250 AI voice minutes, 250 subtitle minutes, 1,000 AI images, 30 custom voices",
      includes: [
        "All templates",
        "Captions in 102 languages",
        "Access to 50+ AI voices",
        "30 custom voice slots",
        "Renews monthly until cancelled",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Monthly figure only. The annual toggle says 25% off but no annual price rendered on the page.",
    },
  ],
  entryPlanName: "Lite",
  entryPlanIncludes:
    "300 AI Studio credits, 30 export minutes, 30 voice minutes, 30 subtitle minutes, 100 AI images and 1 custom voice, monthly only",
  freeTier: "None. The refund policy states there is no free plan.",
  trial:
    "None advertised. The refund policy says a trial exists only where a checkout page states one, and an uncancelled trial converts and is not refundable.",
  meteringUnit:
    "Six meters: export minutes, AI voice minutes, subtitle minutes, AI images, custom voice slots and AI Studio credits",
  exportResolution: "Not published on the site.",
  features: {
    longToShort: { v: "no", note: "Split Videos needs both clips supplied" },
    momentRanking: { v: "no", note: "Top 5 Moments is a manual countdown" },
    reframing: { v: "no", note: "Not on any product page" },
    maxSource: { v: "n/a", note: "Not published; export metered in minutes" },
    layouts: { v: "text", note: "Split top/bottom or left/right only" },
    redditStory: { v: "yes", note: "Reddit Videos and AI Story templates" },
    fakeText: { v: "yes", note: "Fake Text Videos template" },
    aiImages: { v: "yes", note: "100 to 1,000 a month by plan" },
    voiceover: { v: "yes", note: "50+ voices, 30 to 250 minutes by plan" },
    voiceCloning: { v: "partial", note: "Custom voice slots, import from ElevenLabs" },
    avatars: { v: "n/a", note: "Not on the site" },
    timelineEditor: { v: "no", note: "Per-template Edit tabs only" },
    captionStyles: { v: "n/a", note: "Count not published" },
    captionLanguages: { v: "text", note: "102 languages" },
    bRoll: { v: "partial", note: "AI Studio generations, no stock library" },
    fourK: { v: "n/a", note: "Resolution not published" },
    scheduling: { v: "no", note: "Not on product pages" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "None published" },
    webhooks: { v: "n/a", note: "Not on the site" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Not advertised" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "n/a", note: "Not on the pricing page" },
    freeTier: { v: "no", note: "Refund policy: there is no free plan" },
    trial: { v: "partial", note: "Only where a checkout page says so" },
    metering: { v: "text", note: "Six meters: minutes, images, voices, credits" },
    rollover: { v: "n/a", note: "Not stated on the pricing page" },
    refunds: { v: "text", note: "All sales final, listed exceptions" },
    cancelInApp: { v: "n/a", note: "Mechanism not described; access to period end" },
  },
  billingTerms: {
    refunds:
      "All sales are final, per the refund policy, except duplicate charges, renewals after a cancellation, mispriced or unauthorised charges, or where the law requires. UK and EEA 14-day withdrawal rights are waived on first access. Failed generations are refunded as credits, not cash. Unjustified chargebacks can suspend the account.",
    cancel:
      "The pricing page says every plan renews monthly until cancelled and the refund policy says access continues to the end of the paid period after cancellation. Neither page describes where the cancel control is. Billing runs through Whop.",
    sourceUrl: REFUNDS,
    checkedAt: CHECKED,
  },
  quotes: [],
  theirCrayoPages: [
    {
      url: "https://clippie.ai/blog/clippie-ai-vs-crayo-ai-the-best-tool-for-faceless-viral-video-creation-in",
      date: "2025-05-09",
      claims: [
        "Crayo lacks the ability to create iMessage-style or WhatsApp-style fake text message story videos",
        "Users cannot fully control layout design, video pacing, or advanced formatting",
        "It does not allow for the viral Would You Rather video format",
        "The Top 5 format is not offered at all in Crayo's platform",
        "Crayo doesn't support importing custom MP3 files",
        "Would You Rather is unique to Clippie AI",
      ],
    },
    {
      url: "https://clippie.ai/blog/best-crayo-ai-alternative-2026",
      date: "2026-07-13",
      claims: [
        "Crayo meters usage in workflow credits, 50 per month on the entry plan, 150 and 250 above",
        "Export limits of 40 minutes, 2 hours and 4 hours by plan",
        "As of 2026, Crayo offers no free tier and no free trial",
        "The advertised price requires a 12-month commitment",
        "Trustpilot reviewers have reported being charged after cancelling",
        "Crayo's annual Hobby plan is cheaper than Clippie Lite",
      ],
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: "Pricing page fetched directly on Oct 8, 2026. Monthly figures for Lite, Creator and Pro and the six per-plan meters were read from the page. The annual toggle is labelled 25% off but no annual figure rendered in the fetched HTML, so annual fields are omitted; a browser check of the toggle is still needed. A VideoSDK listing quotes Pro at the same 69.99 figure in GBP, which is not used here. Refund, trial and cancellation facts come from clippie.ai/refund-policy, fetched the same day.",
  },
  seoPattern: "clippie.ai/blog/<long-keyword-slug>, including two posts about Crayo; no /vs/ or /alternatives/ directory",
};
